import { AxiosError } from 'axios';
import Joi from 'joi';
import { cleanObject } from '../../utils/cleanObject';
import api from '../api';
import v2api from '../apiV2';

const flightBriefSchema = Joi.object({
  bookingId: Joi.number().required(),
  flightDuration: Joi.string().allow(null, ''),
  flightNumber: Joi.string().allow(null, ''),
  aircraft: Joi.object({
    type: Joi.string().allow(null, ''),
    registration: Joi.string().allow(null, ''),
    seatNumber: Joi.number().allow(null, ''),
  }),
  departureTerminalId: Joi.number().allow(null),
  departureMeetingInfo: Joi.string().allow(null, ''),
  arrivalTerminalId: Joi.number().allow(null),
  arrivalMeetingInfo: Joi.string().allow(null, ''),
  passengersList: Joi.array().items(Joi.object({
    category: Joi.string().allow(null, ''),
    fullname: Joi.string().allow(null, ''),
    id: Joi.number().allow(null, ''),
    identityFiles: Joi.array().items(Joi.string()).allow(null),
  })),
  information: Joi.string().allow(null, ''),
  flightRadarLink: Joi.string().uri().allow(null, ''),
  dutyOfficer: Joi.object({
    id: Joi.alternatives().try(Joi.number(), Joi.string()).allow(null),
    firstname: Joi.string().allow(null, ''),
    lastname: Joi.string().allow(null, ''),
    avatar: Joi.string().allow(null, ''),
    phone: Joi.string().allow(null, ''),
    email: Joi.string().allow(null, ''),
  }).allow(null),
  displayCompanyLogo: Joi.boolean(),
});

const sharedFlightBriefSchema = Joi.object({
  uuid: Joi.string().required(),
  departure: Joi.object({
    airport: Joi.object({
      city: Joi.string().allow(null, ''),
      code: Joi.string().allow(null, ''),
      airportCode: Joi.string().allow(null, ''),
    }),
    date: Joi.string(),
    time: Joi.string().allow(null, ''),
    utc: Joi.string().allow(null, ''),
  }),
  arrival: Joi.object({
    airport: Joi.object({
      city: Joi.string().allow(null, ''),
      code: Joi.string().allow(null, ''),
      airportCode: Joi.string().allow(null, ''),
    }),
    date: Joi.string(),
    time: Joi.string().allow(null, ''),
    utc: Joi.string().allow(null, ''),
  }),
  flightDuration: Joi.string().allow(null, ''),
  flightNumber: Joi.string().allow(null, ''),
  aircraft: Joi.object({
    type: Joi.string().allow(null, ''),
    registration: Joi.string().allow(null, ''),
    seatNumber: Joi.number().allow(null, ''),
  }),
  departureTerminal: Joi.object({
    name: Joi.string().allow(null, ''),
    terminalId: Joi.string().allow(null, ''),
    identityCode: Joi.string().allow(null, ''),
    address: Joi.string().allow(null, ''),
    coordinate: Joi.array().items(Joi.string().allow(null, '')).length(2).allow(null),
    phone: Joi.string().allow(null, ''),
    openingTime: Joi.string().allow(null, ''),
    closingTime: Joi.string().allow(null, ''),
    meetingInfo: Joi.string().allow(null, ''),
  }),
  arrivalTerminal: Joi.object({
    name: Joi.string().allow(null, ''),
    terminalId: Joi.string().allow(null, ''),
    identityCode: Joi.string().allow(null, ''),
    address: Joi.string().allow(null, ''),
    coordinate: Joi.array().items(Joi.string().allow(null, '')).length(2).allow(null),
    phone: Joi.string().allow(null, ''),
    openingTime: Joi.string().allow(null, ''),
    closingTime: Joi.string().allow(null, ''),
    meetingInfo: Joi.string().allow(null, ''),
  }),
  extra: Joi.object({
    catering: Joi.string().allow(null, ''),
    transport: Joi.string().allow(null, ''),
    other: Joi.string().allow(null, ''),
  }).allow(null),
  passengers: Joi.object({
    adults: Joi.number().allow(null),
    children: Joi.number().allow(null),
    infants: Joi.number().allow(null),
    animals: Joi.number().allow(null),
  }),
  luggage: Joi.object({
    s: Joi.number().allow(null),
    m: Joi.number().allow(null),
    l: Joi.number().allow(null),
    xl: Joi.number().allow(null),
  }),
});

/**
 * Create a new flight brief
 * @param {FlightBrief} brief
 * @returns {Promise<any>} - 201 return code
 */
export const createFlightBrief = async (brief) => {
  const cleanBody = cleanObject(brief);
  const result = await api.post('/flight-briefs', cleanBody);
  return result.status;
};

/**
 * Update a flight brief
 * @param {FlightBrief} brief
 * @returns {Promise<any>} - 204 return code
 */
export const updateFlightBrief = async (brief) => {
  const cleanBody = cleanObject(brief);
  const result = await api.put(`/flight-briefs/${brief.uuid}`, cleanBody);
  return result.status;
};

/**
 * Get a flight brief by its uuid
 * @param {string} uuid - The flight brief id.
 * @returns {Promise<FlightBrief>} - The flight brief.
 */
export const getFlightBrief = async (uuid) => {
  const result = await v2api.get(`/flight-briefs/${uuid}`);
  try {
    await flightBriefSchema.validateAsync(result.data, { allowUnknown: true });
  } catch (/** @type {any} */ error) {
    throw new AxiosError('', `API response do not match flight brief Schema : ${error.message}`);
  }
  return result.data;
};

/**
 * Get a flight brief shared version by its uuid
 * @param {string} uuid - The flight brief id.
 * @returns {Promise<SharedFlightBrief>} - The flight brief to share.
 */
export const getSharedFlightBrief = async (uuid) => {
  const result = await api.get(`/public/flight-briefs/${uuid}`);
  try {
    await sharedFlightBriefSchema.validateAsync(result.data, { allowUnknown: true });
  } catch (/** @type {any} */ error) {
    throw new AxiosError('', `API response do not match flight brief Schema : ${error.message}`);
  }
  return result.data;
};
